import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { Close, DocumentView } from '@carbon/react/icons';
import { useAtomValue } from 'jotai';
import { InterpretationForm } from 'components/interpretation-form/interpretation-form';
import {
  useDocumentAtom,
  useInterpretationsAtom,
} from 'pages/documents/common/atoms';
import { OwnershipGraph, DocumentRecord, ConveyanceEdge } from './types';
import { getEdges } from './data-helpers';
import { TransactionSection, TransactionData } from './transaction-section';
import styles from './document-review-panel.module.scss';
import resultsStyles from './v2-results-view.module.scss';
import { isImageInlineRenderable } from 'utils/document-image';

type TabId = 'validation' | 'interpretation';

const TABS: { id: TabId; label: string }[] = [
  { id: 'validation', label: 'Validation' },
  { id: 'interpretation', label: 'Interpretation' },
];

interface DocumentReviewPanelProps {
  documentId: number;
  onBack?: () => void;
  ownershipGraph: OwnershipGraph;
  documents: Record<string, DocumentRecord>;
  startDrag: (
    axis: 'horizontal' | 'vertical',
    containerRef: React.RefObject<HTMLDivElement>,
    setter: (ratio: number) => void
  ) => void;
}

export function DocumentReviewPanel({
  documentId,
  onBack,
  ownershipGraph,
  documents,
  startDrag,
}: DocumentReviewPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>('validation');
  const [showPdf, setShowPdf] = useState(true);
  const [splitRatio, setSplitRatio] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Data fetching for interpretation tab ──────────────────────────────────

  const documentAtom = useDocumentAtom(`/documents/${documentId}`);
  const docResource = useAtomValue(documentAtom);
  const docData =
    docResource?.data && !('error' in docResource.data)
      ? docResource.data
      : null;

  const interpretationsHref = docData?.resources?.interpretations?.href;
  const interpretationsAtom = useInterpretationsAtom(interpretationsHref);
  const intResource = useAtomValue(interpretationsAtom);
  const interpretations = useMemo(() => {
    return intResource?.data && Array.isArray(intResource.data)
      ? intResource.data
      : [];
  }, [intResource?.data]);

  const refetchInterpretations = useCallback(() => {
    // The atom auto-refetches when href changes; this is a no-op placeholder
    // matching the InterpretationForm prop signature
  }, []);

  const role = useMemo(() => {
    if (interpretations.length > 0) {
      return { type: 'update' as const, href: interpretations[0].href };
    }
    return {
      type: 'create' as const,
      href: docData?.resources?.interpretations?.href ?? '',
    };
  }, [interpretations, docData]);

  // PDF image URL
  const imageHref = docData?.resources?.image?.href;
  const imageContentType = docData?.resources?.image?.contentType;
  const imageFilename = docData?.resources?.image?.filename;
  const imageInlineRenderable = isImageInlineRenderable(imageContentType);

  // ── Validation tab: group edges for this document ─────────────────────────

  const documentEdges = useMemo(() => {
    return getEdges(ownershipGraph).filter((e) => e.documentId === documentId);
  }, [ownershipGraph, documentId]);

  // Group edges by transaction — builds TransactionData for the shared component
  const transactions = useMemo((): TransactionData[] => {
    const txMap = new Map<number, ConveyanceEdge[]>();
    for (const edge of documentEdges) {
      const txId = edge.transactionId ?? 0;
      if (!txMap.has(txId)) txMap.set(txId, []);
      txMap.get(txId)!.push(edge);
    }
    return Array.from(txMap.entries()).map(([txId, edges]) => {
      const conveyors: { name: string; entityId: number }[] = [];
      const conveyees: { name: string; entityId: number }[] = [];
      const seenConveyors = new Set<number>();
      const seenConveyees = new Set<number>();
      for (const edge of edges) {
        const fromNode = edge.fromNodeId
          ? ownershipGraph.nodes[edge.fromNodeId]
          : null;
        const toNode = edge.toNodeId
          ? ownershipGraph.nodes[edge.toNodeId]
          : null;
        if (
          fromNode &&
          !edge.params?.retained &&
          !seenConveyors.has(fromNode.entity.entityId)
        ) {
          seenConveyors.add(fromNode.entity.entityId);
          conveyors.push({
            name: fromNode.entity.entityName,
            entityId: fromNode.entity.entityId,
          });
        }
        if (
          toNode &&
          !edge.params?.retained &&
          !seenConveyees.has(toNode.entity.entityId)
        ) {
          seenConveyees.add(toNode.entity.entityId);
          conveyees.push({
            name: toNode.entity.entityName,
            entityId: toNode.entity.entityId,
          });
        }
      }
      return {
        transactionId: txId,
        edges,
        effectiveDate: edges[0]?.transactionDate ?? '',
        conveyors,
        conveyees,
        conveyedEstates: [],
      };
    });
  }, [documentEdges, ownershipGraph.nodes]);

  const docRecord = documents[String(documentId)];

  // Flattened the image-pane branches (inline-renderable iframe / download-only /
  // loading / unavailable) into early returns — clearer than a nested ternary.
  const renderImagePane = () => {
    if (imageHref) {
      if (imageInlineRenderable) {
        return (
          <iframe
            src={imageHref}
            className={styles.iframe}
            style={isDragging ? { pointerEvents: 'none' } : undefined}
            title={docRecord?.documentTitle ?? `Document ${documentId}`}
          />
        );
      }
      return (
        <div className={styles.downloadOnly}>
          <p>{imageFilename ?? 'Document file'}</p>
          <Button
            kind="primary"
            href={imageHref}
            target="_blank"
            rel="noopener"
          >
            Download
          </Button>
        </div>
      );
    }
    if (docResource?.loading || !docData) {
      return (
        <div className={styles.loadingContainer}>
          <InlineLoading description="Loading document..." />
        </div>
      );
    }
    return <div className={styles.noImage}>No document image available</div>;
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.reviewPanelContainer} ref={containerRef}>
      {/* Document header — spans full width above the split */}
      <div className={styles.docHeader}>
        <span className={styles.headerTitle}>Document Review</span>
        <div className={styles.headerActions}>
          <div className={styles.docToggle}>
            <Button
              kind={showPdf ? 'primary' : 'ghost'}
              size="sm"
              hasIconOnly
              renderIcon={DocumentView}
              iconDescription={showPdf ? 'Hide document' : 'Show document'}
              onClick={() => setShowPdf((v) => !v)}
            />
          </div>
          {onBack && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onBack}
              aria-label="Back to timeline"
            >
              <Close />
            </button>
          )}
        </div>
      </div>
      {/* Split pane: left (tabs + content) | right (PDF) */}
      <div className={styles.splitPane}>
        {/* Left pane: tabs + content */}
        <div
          className={styles.leftPane}
          style={
            splitRatio !== null
              ? { flex: `0 0 ${splitRatio * 100}%` }
              : undefined
          }
        >
          {/* Tab bar */}
          <div className={styles.tabBar} role="tablist">
            {TABS.map((tab, i) => (
              <React.Fragment key={tab.id}>
                {i === 1 && <div className={styles.tabSeparator} />}
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`${styles.tab} ${
                    activeTab === tab.id ? styles.active : ''
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Tab content — both tabs always mounted, hidden via display:none */}
          <div
            className={styles.tabContent}
            style={activeTab !== 'validation' ? { display: 'none' } : undefined}
          >
            <div className={styles.validationContent}>
              {transactions.length === 0 ? (
                <div className={styles.emptyState}>
                  No validated operations for this document
                </div>
              ) : (
                transactions.map((txn) => (
                  <TransactionSection
                    key={txn.transactionId}
                    txn={txn}
                    nodeMap={ownershipGraph.nodes}
                    documentId={documentId}
                    defaultOpsExpanded
                  />
                ))
              )}
            </div>
          </div>

          <div
            className={styles.tabContent}
            style={
              activeTab !== 'interpretation' ? { display: 'none' } : undefined
            }
          >
            <div className={styles.interpretationWrapper}>
              {docResource?.loading ? (
                <div className={styles.loadingContainer}>
                  <InlineLoading description="Loading document..." />
                </div>
              ) : !docData ? (
                <div className={styles.emptyState}>
                  Could not load document data
                </div>
              ) : intResource?.loading ? (
                <div className={styles.loadingContainer}>
                  <InlineLoading description="Loading interpretations..." />
                </div>
              ) : (
                <InterpretationForm
                  key={documentId}
                  document={docData}
                  interpretations={interpretations}
                  role={role}
                  refetchInterpretations={refetchInterpretations}
                />
              )}
            </div>
          </div>
        </div>

        {showPdf && (
          <>
            {/* Drag handle — reuses parent's shared style */}
            <div
              className={resultsStyles.dragHandleVertical}
              onMouseDown={() => {
                setIsDragging(true);
                const onUp = () => {
                  setIsDragging(false);
                  document.removeEventListener('mouseup', onUp);
                };
                document.addEventListener('mouseup', onUp);
                startDrag('horizontal', containerRef, setSplitRatio);
              }}
            />

            {/* Right pane: PDF viewer */}
            <div
              className={styles.iframePane}
              style={
                splitRatio !== null
                  ? { flex: `0 0 ${(1 - splitRatio) * 100}%` }
                  : undefined
              }
            >
              {renderImagePane()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
